'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface ImpactMetricsProps {
  currentLanguage: string;
}

const ImpactMetrics = ({ currentLanguage }: ImpactMetricsProps) => {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const content = {
    en: {
      title: 'Real-Time Impact Dashboard',
      subtitle: 'Track our collective environmental impact across regions',
      metrics: [
        {
          icon: 'CloudIcon',
          value: '2,547,892',
          unit: 'Tons',
          label: 'CO₂ Sequestered',
          trend: '+12.5%',
          color: 'text-primary'
        },
        {
          icon: 'GlobeAltIcon',
          value: '156',
          unit: 'Active',
          label: 'Projects Running',
          trend: '+8',
          color: 'text-secondary'
        },
        {
          icon: 'UserGroupIcon',
          value: '847',
          unit: 'Partners',
          label: 'Organizations',
          trend: '+23',
          color: 'text-accent'
        },
        {
          icon: 'MapPinIcon',
          value: '12',
          unit: 'Countries',
          label: 'Global Reach',
          trend: '+2',
          color: 'text-trust'
        }
      ]
    },
    hi: {
      title: 'रीयल-टाइम प्रभाव डैशबोर्ड',
      subtitle: 'क्षेत्रों में हमारे सामूहिक पर्यावरणीय प्रभाव को ट्रैक करें',
      metrics: [
        {
          icon: 'CloudIcon',
          value: '2,547,892',
          unit: 'टन',
          label: 'CO₂ संग्रहित',
          trend: '+12.5%',
          color: 'text-primary'
        },
        {
          icon: 'GlobeAltIcon',
          value: '156',
          unit: 'सक्रिय',
          label: 'चल रही परियोजनाएं',
          trend: '+8',
          color: 'text-secondary'
        },
        {
          icon: 'UserGroupIcon',
          value: '847',
          unit: 'भागीदार',
          label: 'संगठन',
          trend: '+23',
          color: 'text-accent'
        },
        {
          icon: 'MapPinIcon',
          value: '12',
          unit: 'देश',
          label: 'वैश्विक पहुंच',
          trend: '+2',
          color: 'text-trust'
        }
      ]
    },
    ar: {
      title: 'لوحة التأثير في الوقت الفعلي',
      subtitle: 'تتبع تأثيرنا البيئي الجماعي عبر المناطق',
      metrics: [
        {
          icon: 'CloudIcon',
          value: '2,547,892',
          unit: 'طن',
          label: 'CO₂ محتجز',
          trend: '+12.5%',
          color: 'text-primary'
        },
        {
          icon: 'GlobeAltIcon',
          value: '156',
          unit: 'نشط',
          label: 'مشاريع قيد التشغيل',
          trend: '+8',
          color: 'text-secondary'
        },
        {
          icon: 'UserGroupIcon',
          value: '847',
          unit: 'شركاء',
          label: 'منظمات',
          trend: '+23',
          color: 'text-accent'
        },
        {
          icon: 'MapPinIcon',
          value: '12',
          unit: 'دول',
          label: 'الوصول العالمي',
          trend: '+2',
          color: 'text-trust'
        }
      ]
    }
  };

  const currentContent = content[currentLanguage as keyof typeof content] || content.en;

  return (
    <section className="py-16 md:py-24 bg-surface">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center space-y-4 mb-12">
          <h2 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-text-primary">
            {currentContent.title}
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            {currentContent.subtitle}
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {currentContent.metrics.map((metric, index) => (
            <div
              key={index}
              className="bg-card rounded-xl border border-border p-6 shadow-natural hover:shadow-natural-lg transition-all duration-300 ease-organic"
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`w-12 h-12 rounded-lg bg-surface flex items-center justify-center ${metric.color}`}>
                  <Icon name={metric.icon as any} size={24} />
                </div>
                <span className="text-xs font-semibold text-success bg-success/10 px-2 py-1 rounded">
                  {metric.trend}
                </span>
              </div>

              <div className="space-y-1">
                <div className="flex items-baseline space-x-2">
                  <span className="font-heading font-bold text-3xl text-text-primary">
                    {metric.value}
                  </span>
                  <span className="text-sm font-medium text-text-secondary">
                    {metric.unit}
                  </span>
                </div>
                <p className="text-sm text-text-secondary">
                  {metric.label}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 bg-card rounded-xl border border-border p-8 shadow-natural">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Icon name="MapIcon" size={20} className="text-primary" />
                <span className="font-heading font-semibold text-text-primary">Regional Distribution</span>
              </div>
              <div className="space-y-1 pl-7">
                <div className="flex justify-between text-sm">
                  <span className="text-text-secondary">India</span>
                  <span className="font-semibold text-text-primary">68 Projects</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-text-secondary">UAE</span>
                  <span className="font-semibold text-text-primary">42 Projects</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-text-secondary">Others</span>
                  <span className="font-semibold text-text-primary">46 Projects</span>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Icon name="ChartBarIcon" size={20} className="text-secondary" />
                <span className="font-heading font-semibold text-text-primary">Project Types</span>
              </div>
              <div className="space-y-1 pl-7">
                <div className="flex justify-between text-sm">
                  <span className="text-text-secondary">Reforestation</span>
                  <span className="font-semibold text-text-primary">45%</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-text-secondary">Renewable Energy</span>
                  <span className="font-semibold text-text-primary">32%</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-text-secondary">Water Conservation</span>
                  <span className="font-semibold text-text-primary">23%</span>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Icon name="TrophyIcon" size={20} className="text-accent" />
                <span className="font-heading font-semibold text-text-primary">Achievements</span>
              </div>
              <div className="space-y-1 pl-7">
                <div className="flex items-start space-x-2 text-sm">
                  <Icon name="CheckCircleIcon" size={16} className="text-success mt-0.5 flex-shrink-0" />
                  <span className="text-text-secondary">ISO 14064 Certified</span>
                </div>
                <div className="flex items-start space-x-2 text-sm">
                  <Icon name="CheckCircleIcon" size={16} className="text-success mt-0.5 flex-shrink-0" />
                  <span className="text-text-secondary">Gold Standard Verified</span>
                </div>
                <div className="flex items-start space-x-2 text-sm">
                  <Icon name="CheckCircleIcon" size={16} className="text-success mt-0.5 flex-shrink-0" />
                  <span className="text-text-secondary">UN SDG Aligned</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ImpactMetrics;